<template>
    <div :class="[{'full-viewport': full_viewport }, 'tw-flex bg-color']" id="room" ref="room">
        <div
            :class="`tw-relative ${full_viewport ? 'bg-color-full-viewport' : 'bg-color'} ${show_sidepanel ? 'tw-w-8/12 lg:tw-w-9/12 xl:tw-w-9/12' : 'tw-w-full'}`">
            <room-header
                :key="header_data_has_changed"
                :number_of_participants="number_of_participants"
                :status_is_live="status_is_live"
                :name="header_name"
                :speaking="header_name ? speaking && this.publishers[0].isMicrophoneEnabled : false"
                :show_name="show_header_name"
            />

            <room-videos
                v-if="loaded"
                :debug="debug"
                :fullscreen="fullscreen"
                :full_viewport="full_viewport"
                :live_room="live_room"
                :live_room_participant="live_room_participant"
                :livekit_room="livekit_room"
                :publishers="publishers"
                :is_connected="is_connected"

                ref="room_videos"
            />

            <room-toolbar
                v-if="loaded"
                :debug="debug"
                :key="room_toolbar_data_has_changed"
                :fullscreen="fullscreen"
                :full_viewport="full_viewport"
                :live_room="live_room"
                :live_room_participant="live_room_participant"
                :livekit_room="livekit_room"
                :sidepanel_is_expanded="show_sidepanel"
                :publishers_length="publishers.length"
                :is_connected="is_connected"

                @expand-sidepanel="handleExpandSidePanel"
                @toggle-fullscreen="handleToggleFullscreen"
            />
        </div>

        <transition name="slide-fade">
            <room-side-panel
                v-if="mount_sidepanel"
                v-show="show_sidepanel"
                :tEvent="tEvent"
                :live_room_participants="live_room.participants"
                :livekit_local_participant="livekit_room.localParticipant"
                class="tw-w-4/12 lg:tw-w-3/12 xl:tw-w-3/12 tw-bg-white"
            />
        </transition>

        <portal-target name="live-modal" slim></portal-target>

        <modal target="live-modal" v-if="popup_open && popup_name === 'co_host_invite'" :title="`${$t('live.participate')}?`" modal_name="co_host_invite" type="medium" class="tw-relative" :align_header_center="true" @close="onInviteDecline">
            <template v-slot:modal-content>
                <div class="tw-flex tw-flex-col tw-justify-center tw-border-t tw-border-gray-200">
                    <div class="tw-flex tw-flex-row tw-mb-5 tw-mt-7 tw-text-center tw-items-center tw-justify-center tw-font-bold">
                        <img class="tw-w-7 tw-w-8 tw-mr-3 tw-rounded-full" :src="co_host_inviter.avatar ? co_host_inviter.avatar : 'https://www.gravatar.com/avatar/865ccc56009fe05c9a393936ae87a19c?s=80&d=mm&r=g'" />
                        {{ co_host_inviter.name }}
                    </div>

                    <div class="tw-text-center tw-mb-6 tw-font-light">{{ $t('live.co_host_invite') }}</div>
                    <div class="tw-flex tw-flex-row tw-font-bold tw-items-center tw-self-center tw-mb-1">
                        <i class="far fa-video tw-text-black tw-mr-4"></i>
                        <p class="tw-text-center">{{ tEvent.title }}</p>
                    </div>
                </div>
            </template>
            <template v-slot:modal-bottom>
                <div class="general-button btn-v2 slim tw-font-bold tw-text-white" @click="onInviteDecline" style="background: #FF5050; min-width: 41%;">
                    {{ $t('live.decline_invite') }}
                </div>
                <div class="general-button btn-v2 slim tw-font-bold tw-text-white" @click="onInviteAccept" style="background: #2196F3; min-width: 52%;">
                    {{ $t('live.accept_invite') }}
                </div>
            </template>
        </modal>
    </div>
</template>

<script>
import RoomChat from "./RoomChat";
import RoomHeader from "./RoomHeader.vue";
import RoomSidePanel from "./RoomSidePanel.vue";
import RoomVideos from "./RoomVideos.vue";
import {getParticipantToken, updateParticipant} from "../../mixins/v2/LiveHelpers";
import {
    AudioPresets,
    MediaDeviceFailure,
    RoomEvent,
    VideoPresets,
    setLogLevel,
    LogLevel,
    Room, Track
} from "livekit-client";
import Toolbar from "./Toolbar.vue";
import Notification from "../../lib/models/Notification";
import Modal from "../../mixins/v2/Modal";
import {EventBus} from "../../lib/event-bus";

export default {
    name: 'Room',
    mixins: [Modal],
    components: {
        'room-chat': RoomChat,
        'room-header': RoomHeader,
        'room-side-panel': RoomSidePanel,
        'room-toolbar': Toolbar,
        'room-videos': RoomVideos,
    },
    data() {
        return {
            debug: process.env.NODE_ENV === 'development' || [92, 4595].includes(tenant_id) || [2239133].includes(user.id),
            fullscreen: false,
            livekit_socket_url: 'wss://www.thehuddlestreaming.nl',
            loaded: false,
            show_sidepanel: false,
            mount_sidepanel: false,
            live_room: null,
            live_room_participant: null,
            livekit_room: null,
            cohost_requester: null,
            user: window.user,
            tEvent: null,
            is_connected: false,
            publishers: [], //participants that can publish
            speaking: false,
            co_host_inviter: null,

            header_data_has_changed: 0,
            room_toolbar_data_has_changed: 0,
        }
    },
    props: {
        tEvent: {
            default: null,
        },
        full_viewport: {
            default: false,
            type: Boolean,
        }
    },
    computed: {
        status_is_live() {
            if (!this.live_room) {
                return false;
            }

            return this.live_room.status === 'STARTED';
        },

        number_of_participants() {
            this.header_data_has_changed;

            if (!this.livekit_room) {
                return 0;
            }

            if(this.livekit_room.participants.size === 0 && !this.live_room_participant.can_publish) {
                return 1;
            }

            return this.livekit_room.participants.size;
        },

        header_name() {
            this.header_data_has_changed;

            if (this.publishers.length === 0) {
                return '';
            }

            const parsed_participant = JSON.parse(this.publishers[0].metadata);
            return parsed_participant.user.firstname + ' ' + parsed_participant.user.lastname;
        },

        show_header_name() {
            this.header_data_has_changed;

            if (this.publishers.length !== 1) {
                return false;
            }

            const tracks = Array.from(this.publishers[0].tracks.values()); // Convert map to array
            const has_active_camera_track = tracks.some(track => track.source === Track.Source.Camera && !track.isMuted);
            return this.publishers.length === 1 && has_active_camera_track;
        }
    },
    methods: {
        async onInviteAccept() {
            await updateParticipant(this.live_room_participant.id,{
                'can_publish': true,
                'can_publish_data': true,
                'room_admin': false,
            });

            this.co_host_inviter = null;
            this.closePopup();
        },
        onInviteDecline() {
            window.Echo.channel(`presence-live-room.${this.tEvent.live_room.id}`).whisper('declinedInvite', {
                inviter_id: this.co_host_inviter.id,
                invited_id: this.live_room_participant.id,
            });

            this.co_host_inviter = null;
            this.closePopup();
        },
        async getEvent() {
            const response = await HuddleEvent.api().fetchBySlug(this.$route.params.event_slug);
            return response.response.data.data;
        },
        async getLiveRoom() {
            if (!this.tEvent || this.tEvent.type !== 'live') return false;
            return LiveRoom.find(this.tEvent.live_room.id);
        },
        async getLiveRoomParticipant() {
            if (!this.tEvent || this.tEvent.type !== 'live' && !this.tEvent.live_room.participants) return false;
            const live_room_participant_id = this.tEvent.live_room.participants.find(p => p.user_id === this.user.id).id
            return LiveRoomParticipant.find(live_room_participant_id);
        },
        async createLivekitRoom() {
            setLogLevel(this.debug ? LogLevel.debug : LogLevel.silent);
            return this.livekit_room = await new Room({
                adaptiveStream: true,
                dynacast: true,
                publishDefaults: {
                    // When using simulcast, LiveKit will publish up to three versions of the stream at various resolutions.
                    simulcast: true,
                    // Other options are music and telephone
                    audioBitrate: AudioPresets.speech,
                    // (Discontinuous Transmission of audio)
                    dtx: true,
                    // Encoding parameters for screen share track
                    // screenShareEncoding: {},
                    // Custom video simulcast layers for screen tracks
                    // Note: the layers need to be ordered from lowest to highest quality
                    screenShareSimulcastLayers: [VideoPresets.h540, VideoPresets.h720],
                    videoSimulcastLayers: [VideoPresets.h540, VideoPresets.h720],
                    screenShareEncoding: {
                        maxBitrate: 6_000_000,
                        maxFramerate: 60,
                    },
                    videoEncoding: {
                        maxBitrate: 3_000_000,
                        maxFramerate: 30,
                    },
                },
                videoCaptureDefaults: {
                    resolution: {
                        width: 1920,
                        height: 1080,
                        frameRate: 30,
                    },
                },
            });
        },
        handleExpandSidePanel() {
            if (!this.mount_sidepanel) {
                this.mount_sidepanel = true;
            }
            this.show_sidepanel = !this.show_sidepanel;
        },
        handleToggleFullscreen() {
            if (this.fullscreen) {
                document.exitFullscreen();
                this.fullscreen = false;
            } else {
                this.$refs.room.requestFullscreen().then(() => {
                    this.fullscreen = true;
                }).catch(err => {
                    if (this.debug) console.log(`Error attempting to enable fullscreen mode: ${err.message} (${err.name})`);
                });
            }
        },
        handleInviteAsHost(event) {
            if(this.live_room_participant.id === event.participant_id){
                this.openPopup('co_host_invite');
                this.co_host_inviter = event.inviter;
            }
        },
        handleDeclinedHostInvite(event) {
            EventBus.$emit('removeInvitedId', event.invited_id);
            if (event.inviter_id === this.live_room_participant.id) {
                Notification.insert({
                    data: [
                        {
                            title: this.$t('live.notification_title_declined_invite'),
                            status: 'error',
                        },
                    ],
                });
            }
        },
        async handleRemoveAsHost(event) {
            if (event.participant_id === this.live_room_participant.id) {
                await updateParticipant(this.live_room_participant.id,  {
                    'can_publish': false,
                    'can_publish_data': false,
                    'room_admin': false,
                });
            }
        },
        async handlePermissionsChanged(event) {
            let livekit_participant;
            const is_local_participant = event.live_room_participant_id === this.live_room_participant.id;

            // Refresh live_room_participant with new permissions
            if (is_local_participant) {
                await LiveRoomParticipant.api().fetchById(this.live_room_participant.id);
                this.live_room_participant = await LiveRoomParticipant.find(this.live_room_participant.id);
                livekit_participant = this.livekit_room.localParticipant;
            } else { // remote participant
                const participants_array = Array.from(this.livekit_room.participants.values());

                livekit_participant = participants_array.find((participant) => {
                    return participant.identity === event.live_room_participant_id.toString()
                })
            }

            // Check if participant is listed as a publisher
            const index = this.publishers.findIndex(p => {
                return p.identity === livekit_participant.identity
            });

            // Add or remove publisher from publisher list
            if (!livekit_participant.permissions.canPublish) {
                this.removePublisher(livekit_participant);
            } else if (index === -1) {
                this.addPublisher(livekit_participant);
            } else {
                this.publishers[index].permissions = livekit_participant.permissions;
            }

            this.room_toolbar_data_has_changed++;
        },
        setupPresenceChannel() {
            if (!this.live_room.presence_channel) {
                this.live_room.presence_channel = window.Echo.join(`live-room.${this.tEvent.live_room.id}`);
                this.live_room.presence_channel
                    .listenForWhisper('inviteAsHost', (event) => this.handleInviteAsHost(event))
                    .listenForWhisper('declinedInvite', (event) => this.handleDeclinedHostInvite(event))
                    .listenForWhisper('removeAsHost', async (event) => this.handleRemoveAsHost(event))
                    .listenForWhisper('withdrewInvite', (event) => this.handleWithdrewInvite(event))
                    .listen('.permissionsChanged', (event) => this.handlePermissionsChanged(event))
                    .error((e) => { if (this.debug) alert(`Show.vue - setupPresenceChannel() - ${e}`); });
            }
        },
        handleWithdrewInvite(event) {
            if(this.live_room_participant.id === event.participant_id){
                this.closePopup('co_host_invite');
                this.co_host_inviter = null;
                Notification.insert({
                    data: [
                        {
                            title: this.$t('live.notification_title_withdrew_invite'),
                            status: 'error',
                        },
                    ],
                });
            }
        },
        async connectToRoom() {
            try {
                await this.livekit_room.connect(this.livekit_socket_url, this.live_room_participant.access_token, {
                    autoSubscribe: true
                }).then(() => {
                    updateParticipant(this.live_room_participant.id,  {
                        connected_at: moment().format('YYYY-MM-DD HH:mm:ss'),
                    })
                });

                this.header_data_has_changed++;
            } catch (e) {
                this.debug && console.log(e);
                // Add another try... catch(e), maybe refresh page if still can't connect/clear token and try again
                await this.livekit_room.connect(this.livekit_socket_url, this.live_room_participant.access_token, {
                    autoSubscribe: true
                });
            }
        },
        setFullViewPortMode(enabled) {
            if (enabled) {
                document.body.style.overflow = 'hidden';
                document.body.style.height = '100vh';
                document.body.style.width = '100vw';
                window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'smooth'
                });
            } else {
                document.body.style.overflow = 'auto';
                document.body.style.height = 'auto';
                document.body.style.width = 'auto';
            }
        },
        setupRoomListeners() {
            this.livekit_room.on(RoomEvent.Connected, () => {
                this.is_connected = true;

                if (this.livekit_room.localParticipant.permissions.canPublish) {
                    this.addPublisher(this.livekit_room.localParticipant);
                }

                this.livekit_room.participants.forEach((participant) => {
                    if (participant.permissions.canPublish) {
                        this.addPublisher(participant)
                    }
                })
            })
            this.livekit_room.on(RoomEvent.ParticipantDisconnected, (participant) => {
                this.header_data_has_changed++;
                this.removePublisher(participant);
            })
            this.livekit_room.on(RoomEvent.ParticipantConnected, (participant) => {
                this.header_data_has_changed++;
                this.$nextTick(() => {
                    if (participant.permissions.canPublish) {
                        this.addPublisher(participant);
                    }
                });
            })
            this.livekit_room.on(RoomEvent.TrackSubscribed, (remoteTrack, remoteTrackPublication, remoteParticipant) => {
                this.header_name_updater++;
                const exists = this.live_room.tracks_subscribed.some(track => track.sid === remoteTrack.sid);
                if (!exists) {
                    const track = Object.assign(remoteTrack, {remote_participant_id: remoteParticipant.sid}, {is_attached: false});
                    this.live_room.tracks_subscribed.push(track);
                }
            })
            this.livekit_room.on(RoomEvent.TrackUnsubscribed, (remoteTrack, remoteTrackPublication, remoteParticipant) => {
                this.header_data_has_changed++;
                const index = this.live_room.tracks_subscribed.findIndex(track => track.sid === remoteTrack.sid);
                if (index !== -1) {
                    this.live_room.tracks_subscribed.splice(index);
                }
            })
            this.livekit_room.on(RoomEvent.TrackMuted, () => {
                this.header_data_has_changed++;
            })
            this.livekit_room.on(RoomEvent.TrackUnmuted, () => {
                this.header_data_has_changed++;
            })
            this.livekit_room.on(RoomEvent.LocalTrackPublished, () => {
                this.header_data_has_changed++;
            })
            this.livekit_room.on(RoomEvent.LocalTrackUnpublished, () => {
                this.header_data_has_changed++;
            })
            this.livekit_room.on(RoomEvent.MediaDevicesError, async (error) => {
                await Notification.insert({
                    data: [
                        {
                            title: 'Device error',
                            body: MediaDeviceFailure.getFailure(error),
                            status: 'error'
                        },
                    ],
                });
            })
            this.livekit_room.on(RoomEvent.ActiveSpeakersChanged, (participants) => {
                this.speaking = participants.length > 0;
            })
        },
        addPublisher(participant) {
            const index = this.publishers.findIndex(p => p.sid === participant.sid);
            if (index === -1) {
                this.publishers.push(participant);
            } else {
                this.publishers[index] = participant;
            }
        },
        removePublisher(participant) {
            const index = this.publishers.findIndex(p => p.identity === participant.identity);
            if (index !== -1) {
                this.publishers.splice(index, 1);
            }
        },
    },
    async mounted() {
        this.$route.meta.disableChat = true;
        this.$router.replace({query: {temp: undefined}}); // notify changed meta attribute

        if (this.full_viewport) {
            this.setFullViewPortMode(true)
        }

        if(!this.tEvent) {
            this.tEvent = await this.getEvent();
        }
        this.live_room = await this.getLiveRoom();
        this.live_room_participant = await this.getLiveRoomParticipant();
        this.livekit_room = await this.createLivekitRoom();
        this.live_room_participant.access_token = await getParticipantToken(this.live_room_participant.access_token, this.live_room_participant.id);

        this.setupRoomListeners();
        this.setupPresenceChannel();
        await this.connectToRoom();

        if (this.debug) window.currentRoom = this.livekit_room;
        this.loaded = true;
    },
    async beforeDestroy() {
        this.setFullViewPortMode(false);
        this.livekit_room.disconnect({stopTracks: true});
    }
};
</script>

<style scoped>
.full-viewport {
    height: 100vh;
    width: 100vw;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 9997;
}

.bg-color {
    background-color: #2F2F2F;
}

.bg-color-full-viewport {
    background-color: #37474F;
}

.slide-fade-enter-active {
    transition: all .15s ease-out;
}

.slide-fade-enter {
    opacity: 0;
}
</style>
